/**
 * @file Hook de validation d'email en temps réel : détection de typos et vérification DNS.
 * @module packages.common.hooks.useEmailValidation
 *
 * Combine deux niveaux de vérification non-bloquants déclenchés sur le blur du champ email :
 * 1. Détection de typos client-side par distance de Levenshtein sur les domaines courants
 * 2. Vérification serveur des enregistrements MX pour les domaines inconnus
 *
 * Les avertissements sont toujours non-bloquants : l'utilisateur peut ignorer les suggestions.
 */

import {useState, useCallback, useRef} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';

/**
 * Domaines email courants utilisés pour la détection de typos.
 * Couvre les providers principaux France, Belgique et internationaux.
 */
const COMMON_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'yahoo.fr', 'yahoo.com', 'yahoo.be',
  'hotmail.fr', 'hotmail.com', 'hotmail.be',
  'outlook.fr', 'outlook.com', 'outlook.be',
  'live.fr', 'live.com', 'live.be',
  'orange.fr', 'sfr.fr', 'free.fr', 'wanadoo.fr', 'laposte.net',
  'bbox.fr', 'numericable.fr',
  'icloud.com', 'me.com',
  'msn.com', 'aol.com',
  'proton.me', 'protonmail.com',
  'skynet.be', 'telenet.be', 'proximus.be',
  'bluewin.ch',
];

/**
 * Calcule la distance de Levenshtein entre deux chaînes.
 *
 * @param {string} a - Première chaîne
 * @param {string} b - Seconde chaîne
 * @return {number} Distance d'édition minimale
 */
function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({length: m + 1}, (_, i) => [i]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp[m][n];
}

/**
 * Recherche un domaine connu proche du domaine fourni (distance ≤ 2).
 *
 * @param {string} domain - Domaine à analyser (ex: "fmail.com")
 * @return {string|null} Domaine suggéré ou null si aucune correspondance
 */
function findDomainSuggestion(domain) {
  const lower = domain.toLowerCase();
  let best = null;
  let bestDist = Infinity;

  for (const known of COMMON_DOMAINS) {
    if (lower === known) return null;
    const dist = levenshtein(lower, known);
    if (dist > 0 && dist <= 2 && dist < bestDist) {
      bestDist = dist;
      best = known;
    }
  }

  return best;
}

/**
 * Valide un email en deux étapes : détection de typo puis vérification DNS.
 *
 * La vérification est déclenchée manuellement via `validateEmail` (typiquement sur onBlur).
 * Les résultats sont non-bloquants : l'utilisateur peut ignorer les avertissements.
 *
 * @return {Object} État et fonctions de validation
 * @return {Object|null} returns.suggestion - Suggestion de correction `{ original, corrected }`
 * @return {boolean} returns.domainError - Vrai si le domaine n'a pas d'enregistrements MX valides
 * @return {boolean} returns.isChecking - Vrai pendant la vérification DNS serveur
 * @return {Function} returns.validateEmail - `(email: string) => Promise<void>` — déclenche la validation
 * @return {Function} returns.buildCorrectedEmail - `(email, correctedDomain) => string` — construit l'email corrigé
 * @return {Function} returns.dismiss - Réinitialise tous les avertissements
 *
 * @example
 * const {suggestion, domainError, validateEmail, buildCorrectedEmail, dismiss} = useEmailValidation();
 *
 * // Déclencher sur le blur du champ
 * <TextField onBlur={() => validateEmail(value)} />
 *
 * // Afficher la suggestion
 * {suggestion && <Alert>Vouliez-vous dire {buildCorrectedEmail(email, suggestion.corrected)} ?</Alert>}
 */
export function useEmailValidation() {
  const [suggestion, setSuggestion] = useState(null);
  const [domainError, setDomainError] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const lastCheckedDomainRef = useRef('');

  const validateEmail = useCallback(async (email) => {
    if (!email || !email.includes('@')) {
      setSuggestion(null);
      setDomainError(false);
      return;
    }

    const domain = email.split('@')[1]?.toLowerCase().trim();
    if (!domain) return;

    // Évite de revalider le même domaine deux fois de suite
    if (domain === lastCheckedDomainRef.current) return;
    lastCheckedDomainRef.current = domain;

    // Étape 1 : détection de typo client-side (instantanée)
    const suggestedDomain = findDomainSuggestion(domain);
    if (suggestedDomain) {
      setSuggestion({original: domain, corrected: suggestedDomain});
      setDomainError(false);
      return;
    }

    // Étape 2 : vérification MX serveur pour les domaines inconnus
    setSuggestion(null);
    setIsChecking(true);
    try {
      const response = await axiosInstance.get(endpoints.public.validation.emailDomain(domain));
      setDomainError(!response.data.valid);
    } catch {
      // Échec silencieux : ne pas bloquer le formulaire si l'API est indisponible
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Construit l'email corrigé en remplaçant le domaine par celui suggéré.
   *
   * @param {string} email - Email original saisi par l'utilisateur
   * @param {string} correctedDomain - Domaine de remplacement
   * @returns {string} Email avec le domaine corrigé
   */
  const buildCorrectedEmail = useCallback((email, correctedDomain) => {
    if (!email || !email.includes('@')) return email;
    const [local] = email.split('@');
    return `${local}@${correctedDomain}`;
  }, []);

  /**
   * Réinitialise tous les avertissements et permet de revalider le même domaine.
   */
  const dismiss = useCallback(() => {
    setSuggestion(null);
    setDomainError(false);
    lastCheckedDomainRef.current = '';
  }, []);

  return {suggestion, domainError, isChecking, validateEmail, buildCorrectedEmail, dismiss};
}
